/**
 * Postcode Validation System
 * Handles header postcode validation and modal interactions
 * Works with custom Shopify app API for postcode validation
 */

class PostcodeValidator {
  constructor() {
    // Skip initialization on cart page
    if (document.querySelector('body')?.classList.contains('template-cart') || 
        window.location.pathname.includes('/cart')) {
      console.log('Postcode validator - Skipping initialization on cart page');
      return;
    }

    // Handle both header form and modal form
    this.form = document.getElementById('postcode-form') || document.getElementById('postcode-form-modal');
    this.input = document.getElementById('postcode-input') || document.getElementById('postcode-input-modal');
    this.result = document.getElementById('postcode-result') || document.getElementById('postcode-result-modal');
    this.submitButton = this.form?.querySelector('button[type="submit"]');
    this.submitText = this.form?.querySelector('.postcode-submit__text');
    this.submitLoading = this.form?.querySelector('.postcode-submit__loading');
    this.result = document.getElementById('postcode-result');
    this.resultMessage = document.querySelector('.postcode-result__message');
    this.successIcon = document.querySelector('.postcode-result__icon--success');
    this.errorIcon = document.querySelector('.postcode-result__icon--error');
    this.postcodeDisplay = document.getElementById('postcode-display');
    this.postcodeDisplayMobile = document.getElementById('postcode-display-mobile');
    this.modal = document.getElementById('PostcodeModal');
    
    console.log('Postcode validator - Initialized', {
      form: !!this.form,
      input: !!this.input,
      modal: !!this.modal,
      postcodeDisplay: !!this.postcodeDisplay
    });
    
    this.init();
  }

  init() {
    // Handle header form
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      this.input?.addEventListener('input', this.handleInput.bind(this));
      this.loadSavedPostcode();
    }

    // Handle modal form if it exists
    const modalForm = document.getElementById('postcode-form-modal');
    const modalInput = document.getElementById('postcode-input-modal');
    if (modalForm && modalInput) {
      modalForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const postcode = modalInput.value.trim();
        if (postcode) {
          // Switch to modal form context
          this.input = modalInput;
          this.form = modalForm;
          this.result = document.getElementById('postcode-result-modal');
          this.resultMessage = this.result?.querySelector('.postcode-result__message');
          this.submitButton = modalForm.querySelector('button[type="submit"]');
          this.submitText = modalForm.querySelector('.postcode-submit__text');
          this.submitLoading = modalForm.querySelector('.postcode-submit__loading');
          this.handleSubmit(e);
        }
      });
      
      // Handle Enter key on modal input
      modalInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          modalForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      });
      
      modalInput.addEventListener('input', this.handleInput.bind(this));
    }
    
    // Also handle Enter key on header form input
    if (this.input) {
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && this.form) {
          e.preventDefault();
          this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      });
    }

    // Setup modal handlers - use setTimeout to ensure DOM is fully ready
    setTimeout(() => {
      this.setupModalClose();
      this.setupModalOpener();
    }, 100);
  }

  handleInput(event) {
    // Auto-format postcode input (uppercase, alphanumeric only)
    const value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    event.target.value = value;
  }

  async handleSubmit(event) {
    event.preventDefault();
    
    const postcode = this.input?.value.trim();
    if (!postcode) return;

    this.setLoading(true);
    this.hideResult();

    try {
      // Validate postcode using custom app API
      const validationResult = await this.validatePostcodeAPI(postcode);
      
      if (validationResult.valid) {
        this.showSuccess('Delivery is available to this postcode!', postcode);
        this.savePostcode(postcode);
      } else {
        this.showError('We don\'t deliver to this postcode yet.');
      }
    } catch (error) {
      console.error('Postcode validation error:', error);
      this.showError('Unable to check postcode. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  async validatePostcodeAPI(postcode) {
    try {
      console.log('Postcode validator - Validating postcode via API:', postcode);
      
      const headers = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };
      
      const requestBody = { 
        postcode: postcode,
        pathway: 'internal__postcode_validation'
      };
      
      const response = await fetch('/apps/logistics/windows', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody)
      });

      // Check Content-Type header to detect HTML responses (misconfigured proxy)
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const textPreview = await response.text().then(t => t.substring(0, 200));
        console.error('Postcode validator - Non-JSON response received:', textPreview);
        throw new Error('Invalid response format from server');
      }

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          // Ignore JSON parse errors
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Postcode validator - API response:', data);
      
      // Expect response format: { valid: true } or { valid: false }
      return {
        valid: data.valid === true
      };
    } catch (error) {
      console.error('Postcode validator - API validation error:', error);
      return { valid: false };
    }
  }

  setLoading(loading) {
    // Find the current form's elements (could be header or modal form)
    const currentForm = this.form;
    if (!currentForm) return;
    
    const submitButton = currentForm.querySelector('button[type="submit"]');
    const submitText = currentForm.querySelector('.postcode-submit__text');
    const submitLoading = currentForm.querySelector('.postcode-submit__loading');
    
    if (submitButton) {
      submitButton.disabled = loading;
      if (loading) {
        submitButton.style.opacity = '0.5';
        if (submitText) submitText.style.setProperty('display', 'none');
        if (submitLoading) {
          // Find the spinner element - check all children for one with 'hidden' class
          let spinner = null;
          const children = Array.from(submitLoading.children);
          
          // First try specific class selectors
          spinner = submitLoading.querySelector('.loading-spinner') || 
                   submitLoading.querySelector('.loading__spinner');
          
          // If not found, look for any div with 'hidden' class
          if (!spinner) {
            spinner = children.find(child => 
              child.classList.contains('hidden') && 
              (child.classList.contains('loading-spinner') || 
               child.classList.contains('loading__spinner') ||
               child.tagName === 'DIV')
            );
          }
          
          // Last resort: get first child div
          if (!spinner && children.length > 0) {
            spinner = children.find(child => child.tagName === 'DIV');
          }
          
          if (spinner) {
            // Remove hidden class from spinner - this is critical!
            const hadHidden = spinner.classList.contains('hidden');
            spinner.classList.remove('hidden');
            // Force display and visibility to override any CSS
            spinner.style.cssText = 'display: inline-block !important; visibility: visible !important; opacity: 1 !important; width: 1.8rem !important; height: 1.8rem !important;';
            
            // Also ensure SVG and path are visible
            const svg = spinner.querySelector('svg.spinner');
            const path = spinner.querySelector('circle.path');
            if (svg) {
              svg.style.cssText = 'display: block !important; visibility: visible !important; width: 100% !important; height: 100% !important;';
            }
            if (path) {
              path.style.cssText = 'visibility: visible !important; opacity: 1 !important;';
            }
            
            const computedStyle = window.getComputedStyle(spinner);
            console.log('Postcode validator - Spinner element found and shown:', {
              tag: spinner.tagName,
              className: spinner.className,
              hadHidden: hadHidden,
              nowHidden: spinner.classList.contains('hidden'),
              computedDisplay: computedStyle.display,
              computedVisibility: computedStyle.visibility,
              computedOpacity: computedStyle.opacity,
              computedWidth: computedStyle.width,
              computedHeight: computedStyle.height,
              svgFound: !!svg,
              pathFound: !!path
            });
          } else {
            console.warn('Postcode validator - Spinner element not found inside loading container');
            console.log('Postcode validator - Loading container children:', children.map(c => ({
              tag: c.tagName,
              className: c.className,
              hasHidden: c.classList.contains('hidden')
            })));
          }
          // Remove hidden class from container and show it
          submitLoading.classList.remove('hidden');
          submitLoading.style.setProperty('display', 'flex', 'important');
          console.log('Postcode validator - Loading spinner container shown');
        } else {
          console.warn('Postcode validator - Loading element not found');
        }
      } else {
        submitButton.style.opacity = '1';
        if (submitText) submitText.style.setProperty('display', 'inline');
        if (submitLoading) {
          // Hide the spinner element - try multiple selectors
          let spinner = submitLoading.querySelector('.loading-spinner');
          if (!spinner) {
            spinner = submitLoading.querySelector('.loading__spinner');
          }
          if (!spinner) {
            spinner = submitLoading.querySelector('div:not(.postcode-submit__loading)');
          }
          
          if (spinner) {
            spinner.classList.add('hidden');
            spinner.style.display = 'none';
          }
          // Hide the container
          submitLoading.classList.add('hidden');
          submitLoading.style.setProperty('display', 'none');
        }
      }
    }
  }

  updateResultIcons(isSuccess) {
    if (!this.result) return;

    this.successIcon = this.result.querySelector('.postcode-result__icon--success');
    this.errorIcon = this.result.querySelector('.postcode-result__icon--error');

    if (this.successIcon) {
      this.successIcon.classList.toggle('hidden', !isSuccess);
    }

    if (this.errorIcon) {
      this.errorIcon.classList.toggle('hidden', isSuccess);
    }
  }

  showSuccess(message, postcode) {
    console.log('Postcode validator - showSuccess called', { message, postcode, result: !!this.result, resultMessage: !!this.resultMessage });
    
    // Find result message in current form context
    if (!this.resultMessage && this.result) {
      this.resultMessage = this.result.querySelector('.postcode-result__message');
    }
    
    if (this.resultMessage) {
      this.resultMessage.textContent = message;
      this.resultMessage.className = 'postcode-result__message';
      console.log('Postcode validator - Result message updated');
    } else {
      console.warn('Postcode validator - Result message element not found');
    }
    
    if (this.result) {
      this.result.style.display = 'flex';
      this.result.className = 'postcode-result';
      this.updateResultIcons(true);
      console.log('Postcode validator - Result container shown');
    } else {
      console.warn('Postcode validator - Result container not found');
    }
    
    this.updatePostcodeDisplay(postcode);
  }

  showError(message) {
    console.log('Postcode validator - showError called', { message, result: !!this.result, resultMessage: !!this.resultMessage });
    
    // Find result message in current form context
    if (!this.resultMessage && this.result) {
      this.resultMessage = this.result.querySelector('.postcode-result__message');
    }
    
    if (this.resultMessage) {
      this.resultMessage.textContent = message;
      this.resultMessage.className = 'postcode-result__message';
      console.log('Postcode validator - Error message updated');
    } else {
      console.warn('Postcode validator - Result message element not found');
    }
    
    if (this.result) {
      this.result.style.display = 'flex';
      this.result.className = 'postcode-result';
      this.updateResultIcons(false);
      console.log('Postcode validator - Error container shown');
    } else {
      console.warn('Postcode validator - Result container not found');
    }
  }

  hideResult() {
    if (this.result) {
      this.result.style.display = 'none';
      this.successIcon = this.result.querySelector('.postcode-result__icon--success');
      this.errorIcon = this.result.querySelector('.postcode-result__icon--error');
      this.successIcon?.classList.add('hidden');
      this.errorIcon?.classList.add('hidden');
    }
  }

  updatePostcodeDisplay(postcode) {
    if (this.postcodeDisplay) {
      this.postcodeDisplay.textContent = postcode;
    }
    if (this.postcodeDisplayMobile) {
      this.postcodeDisplayMobile.textContent = postcode;
    }
  }

  savePostcode(postcode) {
    localStorage.setItem('validated_postcode', postcode);
    
    // Dispatch custom event for delivery widget
    window.dispatchEvent(new CustomEvent('postcodeValidated', {
      detail: { postcode: postcode }
    }));
  }

  loadSavedPostcode() {
    const savedPostcode = localStorage.getItem('validated_postcode');
    if (savedPostcode && this.input) {
      this.input.value = savedPostcode;
      this.updatePostcodeDisplay(savedPostcode);
    }
  }

  setupModalClose() {
    // Close modal on outside click
    if (this.modal) {
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.closeModal();
        }
      });

      // Close button
      const closeButton = this.modal?.querySelector('.postcode-modal__close');
      if (closeButton) {
        closeButton.addEventListener('click', () => this.closeModal());
      }

      // ESC key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal?.hasAttribute('open')) {
          this.closeModal();
        }
      });
    }
  }

  setupModalOpener() {
    // Use event delegation with capture phase to catch clicks before other handlers
    document.addEventListener('click', (e) => {
      // Check if click is on postcode container or any child
      const container = e.target.closest('.postcode_container[data-modal="#PostcodeModal"]');
      if (container) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        console.log('Postcode validator - Container clicked, opening modal');
        this.openModal();
        return false;
      }
    }, true); // Use capture phase

    // Also handle direct clicks on postcode cards
    const postcodeCards = document.querySelectorAll('.postcode-card');
    console.log('Postcode validator - Found postcode cards:', postcodeCards.length);
    postcodeCards.forEach(card => {
      card.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const container = card.closest('.postcode_container[data-modal="#PostcodeModal"]');
        if (container) {
          console.log('Postcode validator - Card clicked, opening modal');
          this.openModal();
        }
        return false;
      }, true); // Use capture phase
    });
  }

  openModal() {
    console.log('Postcode validator - openModal called, modal element:', this.modal);
    
    // Try to find modal if not already found
    if (!this.modal) {
      this.modal = document.getElementById('PostcodeModal');
      console.log('Postcode validator - Re-searching for modal:', this.modal);
    }
    
    if (this.modal) {
      const tagName = this.modal.tagName.toLowerCase();
      console.log('Postcode validator - Modal tag name:', tagName);
      
      if (tagName === 'modal-dialog') {
        this.modal.setAttribute('open', '');
        // Add open class as well for CSS selector matching
        this.modal.classList.add('open');
        console.log('Postcode validator - Modal opened (modal-dialog), attributes set');
        
        // Use requestAnimationFrame to ensure DOM updates before checking
        requestAnimationFrame(() => {
          // Force a reflow to ensure CSS is applied
          void this.modal.offsetHeight;
          // Check if modal is visible
          const computedStyle = window.getComputedStyle(this.modal);
          console.log('Postcode validator - Modal computed display:', computedStyle.display);
          console.log('Postcode validator - Modal computed visibility:', computedStyle.visibility);
          console.log('Postcode validator - Modal z-index:', computedStyle.zIndex);
          
          // If still not visible, force it
          if (computedStyle.display === 'none') {
            console.warn('Postcode validator - Modal still hidden, forcing display');
            this.modal.style.cssText = 'display: flex !important; position: fixed !important; top: 0 !important; left: 0 !important; width: 100% !important; height: 100% !important; z-index: 1000 !important;';
          }
        });
      } else {
        this.modal.classList.add('open');
        this.modal.style.cssText = 'display: flex !important; position: fixed !important; top: 0 !important; left: 0 !important; width: 100% !important; height: 100% !important; z-index: 1000 !important;';
        console.log('Postcode validator - Modal opened (div)');
      }
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      
      // Focus on input if available
      const modalInput = document.getElementById('postcode-input-modal');
      if (modalInput) {
        setTimeout(() => modalInput.focus(), 100);
      }
    } else {
      console.error('Postcode validator - Modal element not found! Searching DOM...');
      const allModals = document.querySelectorAll('[id*="Postcode"], [id*="postcode"]');
      console.log('Postcode validator - Found elements with postcode in ID:', allModals);
    }
  }

  closeModal() {
    if (this.modal) {
      const tagName = this.modal.tagName.toLowerCase();
      if (tagName === 'modal-dialog') {
        this.modal.removeAttribute('open');
        this.modal.classList.remove('open');
        // Remove inline styles to let CSS handle it
        this.modal.style.cssText = '';
      } else {
        this.modal.classList.remove('open');
        this.modal.style.cssText = '';
      }
      // Restore body scroll
      document.body.style.overflow = '';
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new PostcodeValidator();
});

// Make functions globally available
window.PostcodeValidator = PostcodeValidator;
